// Delivery pricing engine — distance × order-size, never free.
//
// PURE, dependency-free module. An identical copy lives at functions/src/pricing.ts
// so the server can re-compute and validate the fee at checkout (clients cannot be
// trusted to send their own delivery fee). Keep the two files in sync.
//
// Two entry points, both feeding the same distance-band × order-size matrix:
//  - quoteFee(...)         : pin/map mode — resolve the band from a distance (km).
//  - quoteFeeForBand(...)  : manual-area mode — the band comes from the chosen area.

import type { Branch, BranchPricing } from "./types";

export interface FeeQuote {
  /** Can this location be delivered to at all (within range)? */
  deliverable: boolean;
  /** Distance in km (pin mode). -1 when unknown (area mode). */
  distanceKm: number;
  /** Delivery fee in KWD (only meaningful when deliverable). */
  deliveryFee: number;
  /** Minimum order for this branch (KWD). */
  minOrder: number;
  /** True when subtotal is below the branch minimum (checkout blocked). */
  belowMin: boolean;
  /** How much more (KWD) the customer must add to reach the minimum. */
  amountToMin: number;
  /** Machine-readable reason when not orderable. */
  reason: "ok" | "outside_area" | "below_min";
  distanceBandIndex: number;
  sizeTierIndex: number;
}

const EARTH_RADIUS_KM = 6371;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance between two lat/lng points, in km. */
export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Approximate road distance from a branch to a point (haversine × road factor). */
export function roadDistanceKm(
  branch: Pick<Branch, "lat" | "lng" | "pricing">,
  point: { lat: number; lng: number },
): number {
  const straight = haversineKm({ lat: branch.lat, lng: branch.lng }, point);
  const km = straight * (branch.pricing.roadFactor || 1.3);
  return Math.round(km * 10) / 10;
}

function distanceBandIndex(pricing: BranchPricing, km: number): number {
  for (let i = 0; i < pricing.distanceBands.length; i++) {
    if (km <= pricing.distanceBands[i]) return i;
  }
  return -1; // beyond the last band => out of range
}

function sizeTierIndex(pricing: BranchPricing, subtotal: number): number {
  for (let i = 0; i < pricing.sizeTiers.length; i++) {
    const t = pricing.sizeTiers[i];
    if (subtotal >= t.min && (t.max === null || subtotal < t.max)) return i;
  }
  return pricing.sizeTiers.length - 1;
}

/** Shared: build a quote once the distance band index is known. */
function buildQuote(
  p: BranchPricing,
  subtotal: number,
  bandIndex: number,
  distanceKm: number,
): FeeQuote {
  const belowMin = subtotal < p.minOrder;
  const amountToMin = belowMin ? round3(p.minOrder - subtotal) : 0;

  if (bandIndex < 0 || bandIndex >= p.distanceBands.length) {
    return {
      deliverable: false,
      distanceKm,
      deliveryFee: 0,
      minOrder: p.minOrder,
      belowMin,
      amountToMin,
      reason: "outside_area",
      distanceBandIndex: -1,
      sizeTierIndex: -1,
    };
  }

  const si = sizeTierIndex(p, subtotal);
  const raw = p.feeMatrix[bandIndex]?.[si] ?? p.feeFloor;
  const deliveryFee = round3(Math.max(p.feeFloor, raw));

  return {
    deliverable: true,
    distanceKm,
    deliveryFee,
    minOrder: p.minOrder,
    belowMin,
    amountToMin,
    reason: belowMin ? "below_min" : "ok",
    distanceBandIndex: bandIndex,
    sizeTierIndex: si,
  };
}

/** Pin mode: quote from an order subtotal (KWD) and a distance (km). */
export function quoteFee(
  branch: Pick<Branch, "pricing">,
  subtotal: number,
  distanceKm: number,
): FeeQuote {
  const p = branch.pricing;
  if (distanceKm > p.maxDistanceKm) {
    return buildQuote(p, subtotal, -1, distanceKm);
  }
  return buildQuote(p, subtotal, distanceBandIndex(p, distanceKm), distanceKm);
}

/** Manual-area mode: quote from an order subtotal and a chosen area's band index. */
export function quoteFeeForBand(
  branch: Pick<Branch, "pricing">,
  subtotal: number,
  bandIndex: number,
): FeeQuote {
  // distanceKm unknown in area mode; report the band's upper bound as an estimate.
  const est = branch.pricing.distanceBands[bandIndex] ?? -1;
  return buildQuote(branch.pricing, subtotal, bandIndex, est);
}

/** Round to 3 decimals (KWD is a 1000-fils currency). */
export function round3(n: number): number {
  return Math.round((n + Number.EPSILON) * 1000) / 1000;
}
